import React, { useState } from "react";
import { useDocuments } from "../../context/DocumentContext";
import { useProjects } from "../../context/ProjectContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { useUnsavedChanges } from "../../hooks/useUnsavedChanges";
import NavigationBlocker from "../../components/common/NavigationBlocker";

// Icons
import { CloudArrowUpIcon, DocumentTextIcon } from "@heroicons/react/24/outline";

const DocumentForm = () => {
  const navigate = useNavigate();
  const { addDocument } = useDocuments();
  const { projects = [] } = useProjects();

  const [isDirty, setIsDirty] = useState(false);
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    documentName: "",
    projectId: "",
    documentType: "drawing",
    description: ""
  });

  const handleChange = (e) => {
    setIsDirty(true);
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setIsDirty(true);
    setFile(e.target.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || !formData.documentName) {
        return toast.error("File and Document Name are required.");
    }

    const data = new FormData();
    data.append("file", file);
    data.append("documentName", formData.documentName);
    data.append("documentType", formData.documentType);
    data.append("description", formData.description);
    if (formData.projectId) data.append("projectId", formData.projectId);
    data.append("userId", 1); 

    try {
        await addDocument(data);
        toast.success("File Uploaded Successfully!");
        setIsDirty(false);
        setTimeout(() => navigate("/admin/DocumentPage"), 1000);
    } catch (err) {
        // Error handled in context
    }
  };

  return (
    <>
      <div className="p-8 bg-gray-50 min-h-screen flex justify-center">
        <div className="w-full max-w-2xl bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100">
          <div className="bg-gray-900 px-8 py-6 text-white flex justify-between items-center">
              <h1 className="text-base font-bold">Upload New Document</h1>
              <CloudArrowUpIcon className="w-8 h-8 text-blue-400" />
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
              
              {/* File Input */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:bg-gray-50 transition">
                  <input type="file" onChange={handleFileChange} className="hidden" id="file-upload" />
                  <label htmlFor="file-upload" className="cursor-pointer flex flex-col items-center">
                      <DocumentTextIcon className="w-12 h-12 text-gray-400 mb-2" />
                      <span className="text-blue-600 font-medium hover:underline">{file ? file.name : "Click to select a file"}</span>
                      <span className="text-xs text-gray-500 mt-1">PDF, Images, CAD drawings supported</span>
                  </label>
              </div>

              {/* Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Document Name</label>
                      <input type="text" name="documentName" onChange={handleChange} className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" placeholder="e.g. Site Plan v2" required />
                  </div>
                  <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">Document Type</label>
                      <select name="documentType" onChange={handleChange} className="w-full border p-2 rounded bg-white outline-none">
                          <option value="drawing">Drawing / Blueprint</option>
                          <option value="contract">Contract</option>
                          <option value="permit">Permit</option>
                          <option value="invoice">Invoice</option>
                          <option value="manual">Manual</option>
                          <option value="other">Other</option>
                      </select>
                  </div>
              </div>

              {/* Project Link */}
              <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Link to Project (Optional)</label>
                  <select name="projectId" onChange={handleChange} className="w-full border p-2 rounded bg-white outline-none">
                      <option value="">-- General Document (No Project) --</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
              </div>

              {/* Description */}
              <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1">Revision Notes / Description</label>
                  <textarea name="description" onChange={handleChange} rows="3" className="w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" placeholder="Details about this version..."></textarea>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4">
                  <button type="button" onClick={() => navigate("/admin/DocumentPage")} className="px-6 py-2 border rounded text-gray-600 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="px-6 py-2 bg-blue-600 text-white rounded font-bold hover:bg-blue-700 shadow-md">Upload Document</button>
              </div>
          </form>
        </div>
      </div>

      {useUnsavedChanges(isDirty)}
      <NavigationBlocker isDirty={isDirty} />
    </>
  );
};

export default DocumentForm;